import { Module, VuexModule, Action } from 'vuex-module-decorators';
import axios from 'axios';
import moment from 'moment';
import { toError, assertOk } from '@/services/normalize';
import {
  BASE_URL,
  GET_RATE,
  M_POST,
  M_GET,
  WALLET_ADDRESS,
  CUSTOMER_GROUP,
  EXPORT_INVOICE,
  M_DELETE
} from '@/utils/api';
import type { ExtAxiosConfig } from '@/middleware/axios-ui';

@Module({ name: 'invoice' })
export default class Invoice extends VuexModule {
  // ============================================================
  // Currency / Rate
  // ============================================================

  @Action({ rawError: true })
  async fetchRate(payload: any) {
    try {
      const { criteria, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + GET_RATE,
        data: criteria,
        method: M_GET,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch currency rate failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch currency rate failed');
    }
  }

  // ============================================================
  // Wallet Address (CRUD)
  // ============================================================

  @Action({ rawError: true })
  async fetchWalletAddress(payload: any) {
    try {
      const { criteria, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + WALLET_ADDRESS,
        data: criteria,
        method: M_GET,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch wallet address failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch wallet address failed');
    }
  }

  @Action({ rawError: true })
  async createWalletAddress(payload: any) {
    try {
      const { body, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + WALLET_ADDRESS,
        data: body,
        method: M_POST,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Create wallet address failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Create wallet address failed');
    }
  }

  @Action({ rawError: true })
  async updateWalletAddress(payload: any) {
    try {
      const { body, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + WALLET_ADDRESS,
        data: body,
        method: M_POST,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Update wallet address failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Update wallet address failed');
    }
  }

  @Action({ rawError: true })
  async deleteWalletAddress(payload: any) {
    try {
      const { body, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + WALLET_ADDRESS,
        data: body,
        method: M_DELETE,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Delete wallet address failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Delete wallet address failed');
    }
  }

  // ============================================================
  // Customer Group (CRD — no Update per backend spec)
  // ============================================================

  @Action({ rawError: true })
  async fetchCustomerGroup(payload: any) {
    try {
      const { criteria, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + CUSTOMER_GROUP,
        data: criteria,
        method: M_GET,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Fetch customer group failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Fetch customer group failed');
    }
  }

  @Action({ rawError: true })
  async createCustomerGroup(payload: any) {
    try {
      const { body, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + CUSTOMER_GROUP,
        data: body,
        method: M_POST,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Create customer group failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Create customer group failed');
    }
  }

  @Action({ rawError: true })
  async deleteCustomerGroup(payload: any) {
    try {
      const { body, __successToast } = payload || {};

      const { data } = await axios({
        url: BASE_URL + CUSTOMER_GROUP,
        data: body,
        method: M_DELETE,
        __successToast
      } as ExtAxiosConfig);

      assertOk(data, 'Delete customer group failed');
      return data?.data;
    } catch (err: any) {
      throw toError(err, 'Delete customer group failed');
    }
  }

  // ============================================================
  // Export Invoice
  // ============================================================

  @Action({ rawError: true })
  async exportInvoice(payload: any) {
    try {
      const { body, __successToast } = payload || {};
      const fileType = body?.fileType;

      const response = await axios({
        url: BASE_URL + EXPORT_INVOICE,
        data: body,
        method: M_POST,
        responseType: 'blob',
        __successToast
      } as ExtAxiosConfig);

      const contentType: string = response?.headers?.['content-type'] || '';
      const blob: Blob = response?.data;

      if (contentType.includes('application/json')) {
        const text = await blob.text();
        let parsed: any = {};

        try {
          parsed = JSON.parse(text);
        } catch (_e) {
          /* leave parsed empty */
        }

        const message = parsed?.status?.message || parsed?.message || 'Export invoice failed';
        throw new Error(message);
      }

      const month = moment(body?.date).format('MMM').toUpperCase();
      const name = String(body?.name || '').toUpperCase();
      const extension = fileType === 'pdf' ? 'pdf' : 'xlsx';
      const filename = `INVOICE-${month}-${name}.${extension}`;

      return { blob, filename, contentType };
    } catch (err: any) {
      throw toError(err, 'Export invoice failed');
    }
  }
}
